import { api } from '../api';

export interface CalendarEvent {
  id?: string;
  summary: string;
  description?: string;
  start: {
    dateTime: string;
    timeZone?: string;
  };
  end: {
    dateTime: string;
    timeZone?: string;
  };
  attendees?: Array<{
    email: string;
    displayName?: string;
    responseStatus?: 'needsAction' | 'declined' | 'tentative' | 'accepted';
  }>;
  reminders?: {
    useDefault: boolean;
    overrides?: Array<{
      method: 'email' | 'popup';
      minutes: number;
    }>;
  };
  location?: string;
  status?: 'confirmed' | 'tentative' | 'cancelled';
  visibility?: 'default' | 'public' | 'private' | 'confidential';
}

export interface CalendarInfo {
  id: string;
  summary: string;
  description?: string;
  primary?: boolean;
  accessRole: 'freeBusyReader' | 'reader' | 'writer' | 'owner';
  timeZone: string;
}

export interface EventoConsulta {
  pacienteNome: string;
  pacienteEmail?: string;
  dataHora: string;
  duracao: number; // em minutos
  observacoes?: string;
  valor?: number;
  consultaId?: string;
}

export interface CalendarServiceResponse {
  sucesso: boolean;
  mensagem: string;
  dados?: any;
  erro?: string;
}

class CalendarService {
  
  /**
   * Verificar status da conexão com Google Calendar
   */
  async verificarConexao(): Promise<CalendarServiceResponse> {
    try {
      // console.log('[CalendarService] Verificando conexão com Google Calendar:', {
        // timestamp: new Date().toISOString()
      // });

      const response = await api.get('/calendar/status');
      
      // console.log('[CalendarService] Status da conexão verificado:', {
        // sucesso: response.data.sucesso,
        // conectado: response.data.dados?.conectado,
        // timestamp: new Date().toISOString()
      // });

      return response.data;

    } catch (error: any) {
      console.error('[CalendarService] Erro ao verificar conexão:', {
        error,
        message: error.response?.data?.mensagem || error.message,
        timestamp: new Date().toISOString()
      });

      // Detectar erros de token expirado
      const isTokenExpired = error.response?.data?.erro?.includes('INVALID_GRANT') ||
                           error.response?.data?.mensagem?.includes('expirado') ||
                           error.response?.data?.mensagem?.includes('refazer a conexão');

      if (isTokenExpired) {
        return {
          sucesso: false,
          mensagem: 'Sua conexão com Google expirou. Clique em "Reconectar" para renovar a autorização.',
          erro: 'TOKEN_EXPIRED'
        };
      }

      return {
        sucesso: false,
        mensagem: error.response?.data?.mensagem || 'Erro ao verificar conexão com Google Calendar',
        erro: error.message
      };
    }
  }

  /**
   * Recarregar tokens do Calendar (útil ao trocar contas Google)
   */
  async recarregarTokens(): Promise<CalendarServiceResponse> {
    try {
      // console.log('[CalendarService] Forçando recarga de tokens:', {
        // timestamp: new Date().toISOString()
      // });

      const response = await api.post('/calendar/recarregar-tokens');
      
      // console.log('[CalendarService] Tokens recarregados:', {
        // sucesso: response.data.sucesso,
        // autenticacaoVerificada: response.data.dados?.autenticacaoVerificada,
        // timestamp: new Date().toISOString()
      // });

      return response.data;

    } catch (error: any) {
      console.error('[CalendarService] Erro ao recarregar tokens:', {
        error,
        message: error.response?.data?.mensagem || error.message,
        timestamp: new Date().toISOString()
      });

      return {
        sucesso: false,
        mensagem: error.response?.data?.mensagem || 'Erro ao recarregar tokens do Calendar',
        erro: error.message
      };
    }
  }

  /**
   * Listar calendários do usuário
   */
  async listarCalendarios(): Promise<CalendarServiceResponse> {
    try {
      // console.log('[CalendarService] Listando calendários:', {
        // timestamp: new Date().toISOString()
      // });

      const response = await api.get('/calendar/calendarios');
      
      // console.log('[CalendarService] Calendários listados:', {
        // sucesso: response.data.sucesso,
        // quantidade: response.data.dados?.length || 0,
        // timestamp: new Date().toISOString()
      // });

      return response.data;

    } catch (error: any) {
      console.error('[CalendarService] Erro ao listar calendários:', {
        error,
        message: error.response?.data?.mensagem || error.message,
        timestamp: new Date().toISOString()
      });

      // Detectar erros de token expirado
      const isTokenExpired = error.response?.data?.erro?.includes('INVALID_GRANT') ||
                           error.response?.data?.mensagem?.includes('expirado') ||
                           error.response?.data?.mensagem?.includes('refazer a conexão');

      if (isTokenExpired) {
        return {
          sucesso: false,
          mensagem: 'Sua conexão com Google expirou. Clique em "Reconectar" para renovar a autorização.',
          erro: 'TOKEN_EXPIRED'
        };
      }

      return {
        sucesso: false,
        mensagem: error.response?.data?.mensagem || 'Erro ao listar calendários',
        erro: error.message
      };
    }
  }

  /**
   * Criar evento no calendário
   */
  async criarEvento(calendarId: string, evento: CalendarEvent): Promise<CalendarServiceResponse> {
    try {
      // console.log('[CalendarService] Criando evento:', {
        // calendarId,
        // summary: evento.summary,
        // start: evento.start.dateTime,
        // end: evento.end.dateTime,
        // timestamp: new Date().toISOString()
      // });

      const response = await api.post('/calendar/evento', {
        calendarId,
        evento
      });
      
      // console.log('[CalendarService] Evento criado:', {
        // sucesso: response.data.sucesso,
        // eventId: response.data.dados?.id,
        // timestamp: new Date().toISOString()
      // });

      return response.data;

    } catch (error: any) {
      console.error('[CalendarService] Erro ao criar evento:', {
        error,
        calendarId,
        eventoSummary: evento.summary,
        message: error.response?.data?.mensagem || error.message,
        timestamp: new Date().toISOString()
      });

      return {
        sucesso: false,
        mensagem: error.response?.data?.mensagem || 'Erro ao criar evento no calendário',
        erro: error.message
      };
    }
  }

  /**
   * Atualizar evento existente
   */
  async atualizarEvento(calendarId: string, eventId: string, evento: CalendarEvent): Promise<CalendarServiceResponse> {
    try {
      // console.log('[CalendarService] Atualizando evento:', {
        // calendarId,
        // eventId,
        // summary: evento.summary,
        // timestamp: new Date().toISOString()
      // });

      const response = await api.put(`/calendar/evento/${eventId}`, {
        calendarId,
        evento
      });
      
      // console.log('[CalendarService] Evento atualizado:', {
        // sucesso: response.data.sucesso,
        // eventId,
        // timestamp: new Date().toISOString()
      // });

      return response.data;

    } catch (error: any) {
      console.error('[CalendarService] Erro ao atualizar evento:', {
        error,
        calendarId,
        eventId,
        message: error.response?.data?.mensagem || error.message,
        timestamp: new Date().toISOString()
      });

      return {
        sucesso: false,
        mensagem: error.response?.data?.mensagem || 'Erro ao atualizar evento no calendário',
        erro: error.message
      };
    }
  }

  /**
   * Excluir evento do calendário
   */
  async excluirEvento(calendarId: string, eventId: string): Promise<CalendarServiceResponse> {
    try {
      // console.log('[CalendarService] Excluindo evento:', {
        // calendarId,
        // eventId,
        // timestamp: new Date().toISOString()
      // });

      const response = await api.delete(`/calendar/evento/${eventId}?calendarId=${encodeURIComponent(calendarId)}`);
      
      // console.log('[CalendarService] Evento excluído:', {
        // sucesso: response.data.sucesso,
        // eventId,
        // timestamp: new Date().toISOString()
      // });

      return response.data;

    } catch (error: any) {
      console.error('[CalendarService] Erro ao excluir evento:', {
        error,
        calendarId,
        eventId,
        message: error.response?.data?.mensagem || error.message,
        timestamp: new Date().toISOString()
      });

      return {
        sucesso: false,
        mensagem: error.response?.data?.mensagem || 'Erro ao excluir evento do calendário',
        erro: error.message
      };
    }
  }

  /**
   * Buscar evento por ID
   */
  async buscarEvento(calendarId: string, eventId: string): Promise<CalendarServiceResponse> {
    try {
      // console.log('[CalendarService] Buscando evento:', {
        // calendarId,
        // eventId,
        // timestamp: new Date().toISOString()
      // });

      const response = await api.get(`/calendar/evento/${eventId}?calendarId=${encodeURIComponent(calendarId)}`);
      
      return response.data;

    } catch (error: any) {
      console.error('[CalendarService] Erro ao buscar evento:', {
        error,
        calendarId,
        eventId,
        message: error.response?.data?.mensagem || error.message,
        timestamp: new Date().toISOString()
      });

      return {
        sucesso: false,
        mensagem: error.response?.data?.mensagem || 'Erro ao buscar evento',
        erro: error.message
      };
    }
  }

  /**
   * Criar evento a partir de dados de consulta
   */
  async criarEventoConsulta(calendarId: string, consulta: EventoConsulta): Promise<CalendarServiceResponse> {
    try {
      // console.log('[CalendarService] Criando evento de consulta:', {
        // calendarId,
        // paciente: consulta.pacienteNome,
        // dataHora: consulta.dataHora,
        // duracao: consulta.duracao,
        // timestamp: new Date().toISOString()
      // });

      const response = await api.post('/calendar/consulta', {
        calendarId,
        consulta
      });
      
      // console.log('[CalendarService] Evento de consulta criado:', {
        // sucesso: response.data.sucesso,
        // eventId: response.data.dados?.id,
        // timestamp: new Date().toISOString()
      // });

      return response.data;

    } catch (error: any) {
      console.error('[CalendarService] Erro ao criar evento de consulta:', {
        error,
        calendarId,
        paciente: consulta.pacienteNome,
        message: error.response?.data?.mensagem || error.message,
        timestamp: new Date().toISOString()
      });

      return {
        sucesso: false,
        mensagem: error.response?.data?.mensagem || 'Erro ao criar evento de consulta',
        erro: error.message
      };
    }
  }

  /**
   * Verificar disponibilidade (Free/Busy)
   */
  async verificarDisponibilidade(timeMin: string, timeMax: string, calendarios: string[]): Promise<CalendarServiceResponse> {
    try {
      // console.log('[CalendarService] Verificando disponibilidade:', {
        // timeMin,
        // timeMax,
        // calendarios: calendarios.length,
        // timestamp: new Date().toISOString()
      // });

      const response = await api.post('/calendar/disponibilidade', {
        timeMin,
        timeMax,
        calendarios
      });
      
      return response.data;

    } catch (error: any) {
      console.error('[CalendarService] Erro ao verificar disponibilidade:', {
        error,
        timeMin,
        timeMax,
        message: error.response?.data?.mensagem || error.message,
        timestamp: new Date().toISOString()
      });

      return {
        sucesso: false,
        mensagem: error.response?.data?.mensagem || 'Erro ao verificar disponibilidade',
        erro: error.message
      };
    }
  }

  /**
   * Testar integração criando evento de teste
   */
  async testarIntegracao(calendarId: string): Promise<CalendarServiceResponse> {
    try {
      // console.log('[CalendarService] Testando integração:', {
        // calendarId,
        // timestamp: new Date().toISOString()
      // });

      const response = await api.post('/calendar/teste', {
        calendarId
      });
      
      // console.log('[CalendarService] Teste de integração concluído:', {
        // sucesso: response.data.sucesso,
        // timestamp: new Date().toISOString()
      // });

      return response.data;

    } catch (error: any) {
      console.error('[CalendarService] Erro no teste de integração:', {
        error,
        calendarId,
        message: error.response?.data?.mensagem || error.message,
        timestamp: new Date().toISOString()
      });

      return {
        sucesso: false,
        mensagem: error.response?.data?.mensagem || 'Erro ao testar integração com Google Calendar',
        erro: error.message
      };
    }
  }

  /**
   * Listar eventos do calendário
   */
  async listarEventos(calendarId: string, timeMin?: string, timeMax?: string, maxResults: number = 50): Promise<CalendarServiceResponse> {
    try {
      // console.log('[CalendarService] Listando eventos:', {
        // calendarId,
        // timeMin,
        // timeMax,
        // maxResults,
        // timestamp: new Date().toISOString()
      // });

      // Se não fornecidas, usar datas padrão: últimos 30 dias + próximos 60 dias
      const agora = new Date();
      const defaultTimeMin = timeMin || new Date(agora.getTime() - (30 * 24 * 60 * 60 * 1000)).toISOString();
      const defaultTimeMax = timeMax || new Date(agora.getTime() + (60 * 24 * 60 * 60 * 1000)).toISOString();

      const response = await api.get(`/calendar/listar-eventos/${calendarId}`, {
        params: {
          timeMin: defaultTimeMin,
          timeMax: defaultTimeMax,
          maxResults,
          singleEvents: true,
          orderBy: 'startTime'
        }
      });

      // console.log('[CalendarService] Eventos listados com sucesso:', {
        // quantidade: response.data?.dados?.length || 0,
        // calendarId,
        // periodo: `${new Date(defaultTimeMin).toLocaleDateString('pt-BR')} até ${new Date(defaultTimeMax).toLocaleDateString('pt-BR')}`
      // });

      return {
        sucesso: true,
        mensagem: 'Eventos listados com sucesso',
        dados: response.data?.dados || []
      };

    } catch (error: any) {
      console.error('[CalendarService] Erro ao listar eventos:', {
        error: error.response?.data || error.message,
        calendarId,
        timestamp: new Date().toISOString()
      });

      return {
        sucesso: false,
        mensagem: error.response?.data?.mensagem || 'Erro ao listar eventos do calendário',
        erro: error.message
      };
    }
  }
}

export const calendarService = new CalendarService();
